"use client";

import { useState, useCallback } from "react";
import type { DateRange } from "react-day-picker";
import {
  Phone,
  Mail,
  Globe,
  Minus,
  Plus,
  ChevronRight,
  CalendarDays,
  Users,
  PawPrint,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { accommodationTotal } from "@/lib/utils/pricing-rules";
import { buildWhatsAppLink } from "@/lib/utils/phone";
import type { DatePricingRule } from "@/types";
// WhatsApp ikonu
function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

// Esnek tip - DB veya Frontend tipini kabul eder
interface ListingContactProps {
  listing: {
    id: string;
    title: string;
    capacity: number;
    location: {
      city: string;
      district: string;
    };
    priceInfo: {
      nightlyPrice: number;
      currency: string;
      minimumStay: number;
      cleaningFee?: number;
      pricingRules?: DatePricingRule[];
    };
    houseRules: {
      petsAllowed: boolean;
    };
    owner?: {
      name: string;
      phone?: string;
      whatsapp?: string;
      email?: string;
      website?: string;
    };
  };
  blockedDates?: string[];
  isMobile?: boolean;
}

export function ListingContact({ listing, blockedDates = [], isMobile }: ListingContactProps) {
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [aralikUyarisi, setAralikUyarisi] = useState<string | null>(null);
  const [guests, setGuests] = useState(1);
  const [hasPets, setHasPets] = useState(false);

  const checkIn = dateRange?.from;
  const checkOut = dateRange?.to;
  // Bugünden önceki günleri devre dışı bırakmak için (saat sıfırlanmış)
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Fiyat formatla
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("tr-TR", {
      style: "currency",
      currency: listing.priceInfo.currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formattedPrice = formatCurrency(listing.priceInfo.nightlyPrice);

  // Gece sayısı ve toplam fiyat hesaplama
  const nights =
    checkIn && checkOut
      ? Math.ceil((checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24))
      : 0;
  // Tarih bazlı fiyatlandırma: seçilen aralıkta her gece için geçerli fiyat toplanır
  // (bir gece bir kuralın tarih aralığına denk geliyorsa kuralın fiyatı uygulanır).
  const pricingRules = listing.priceInfo.pricingRules ?? [];
  const totalAccommodation =
    checkIn && checkOut
      ? accommodationTotal(checkIn, checkOut, pricingRules, listing.priceInfo.nightlyPrice)
      : 0;
  // Seçilen aralıkta baz fiyattan farklı (dönemsel) fiyat devreye girdi mi?
  const hasDatePricing =
    nights > 0 && totalAccommodation !== nights * listing.priceInfo.nightlyPrice;
  const cleaningFee = listing.priceInfo.cleaningFee || 0;
  const grandTotal = totalAccommodation + cleaningFee;
  const isMinimumMet = nights === 0 || nights >= listing.priceInfo.minimumStay;

  // Blocked dates'i Date objesine çevir
  const disabledDates = blockedDates.map((d) => new Date(d));

  // Tarih kontrolü - blocked mu?
  const isDateBlocked = (date: Date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return blockedDates.includes(dateStr);
  };

  // ---------------------------------------------------------------------
  // Y-24: DOLU GECELERİ KAPSAYAN ARALIK SEÇİLEBİLİYORDU
  // ---------------------------------------------------------------------
  // Takvim `mode="range"` ile çalışıyor ve `disabled` yalnız TEK GÜNÜN
  // tıklanmasını engelliyor — react-day-picker, iki uç boşsa ARADAKİ dolu
  // günlerin ÜZERİNDEN atlayan bir aralık kurmaya izin veriyor.
  //
  // Somut örnek (canlı veri, Villa NOYAN): 30-31 Ağustos ve 1 Eylül BOŞ,
  // 2-6 Eylül DOLU, 7-11 Eylül BOŞ. Misafir 30 Ağustos ve 11 Eylül'e
  // tıklayabiliyor → 12 gecelik aralık kuruluyor, sayfa 12 gecelik TUTARI
  // gösteriyor ve o tutar ev sahibine giden WhatsApp/e-posta mesajına yazılıyor.
  // Oysa 5 gece dolu.
  //
  // `excludeDisabled` prop'u yerine BURADA kontrol ediliyor: o prop aralığı
  // sessizce sıfırlar, kullanıcı NEDEN olduğunu anlamaz. Burada açık bir
  // uyarı gösterebiliyoruz. Ayrıca `blockedDates` sayfa yüklendikten sonra
  // bayatlayabilir; kontrolün bizim tarafımızda olması tek doğruluk kaynağı.
  const aralikDoluGeceIceriyorMu = (aralik: DateRange | undefined): boolean => {
    if (!aralik?.from || !aralik?.to) return false;
    // Giriş günü DAHİL, çıkış günü HARİÇ — misafir çıkış sabahı ayrılır,
    // o gece konaklanmaz.
    const gun = new Date(aralik.from);
    while (gun < aralik.to) {
      if (isDateBlocked(gun)) return true;
      gun.setDate(gun.getDate() + 1);
    }
    return false;
  };

  const handleDateSelect = (aralik: DateRange | undefined) => {
    if (aralikDoluGeceIceriyorMu(aralik)) {
      setAralikUyarisi("Seçtiğiniz aralıkta dolu gece var. Lütfen boş bir aralık seçin.");
      // Aralığı KABUL ETME ama giriş tarihini koru — kullanıcı çıkışı yeniden
      // seçebilsin, baştan başlamak zorunda kalmasın.
      setDateRange(aralik?.from ? { from: aralik.from, to: undefined } : undefined);
      return;
    }
    setAralikUyarisi(null);
    setDateRange(aralik);
  };

  // WhatsApp ve Email mesajı oluştur
  const getContactMessage = () => {
    let message = `Merhaba, konakla üzerinden "${listing.title}" ilanınız için iletişime geçiyorum.\n\n`;

    if (checkIn && checkOut) {
      message += `Tarihler: ${format(checkIn, "d MMMM yyyy", { locale: tr })} - ${format(checkOut, "d MMMM yyyy", { locale: tr })} (${nights} gece)\n`;
    }

    message += `Misafir Sayısı: ${guests} kişi\n`;

    if (hasPets) {
      message += `Evcil Hayvan: Var\n`;
    }

    if (nights > 0) {
      message += `\nFiyat Detayı:\n`;
      message += `- Konaklama: ${formatCurrency(totalAccommodation)} (${nights} gece${hasDatePricing ? "" : ` × ${formattedPrice}`})\n`;
      if (cleaningFee > 0) {
        message += `- Temizlik Ücreti: ${formatCurrency(cleaningFee)}\n`;
      }
      message += `- Toplam: ${formatCurrency(grandTotal)}\n`;
    }

    message += `\nKonum: ${listing.location.district}, ${listing.location.city}\n`;
    message += `\n---\nkonakla.co üzerinden gönderildi`;

    return message;
  };

  // Numara `wa.me`nin beklediği biçime çevrilemiyorsa null döner ve WhatsApp
  // butonu HİÇ gösterilmez. Kırık bağlantı göstermek en kötü seçenek: misafir
  // iletişim kurduğunu sanır, ev sahibine mesaj hiç ulaşmaz. Ayrıntı ve canlı
  // ölçüm için bkz. lib/utils/phone.ts
  const whatsAppLink = buildWhatsAppLink(listing.owner?.whatsapp, getContactMessage());

  const getEmailLink = () => {
    const subject = encodeURIComponent(`konakla - ${listing.title} İletişim Talebi`);
    const body = encodeURIComponent(getContactMessage());
    return `mailto:${listing.owner?.email}?subject=${subject}&body=${body}`;
  };

  // Contact click tracking (fire & forget)
  const trackContactClick = useCallback(
    (contactType: "phone" | "whatsapp" | "email" | "website") => {
      // API'ye POST request gönder - hata olsa bile kullanıcıyı etkilemez
      fetch(`/api/listings/${listing.id}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contactType }),
      }).catch(() => {
        // Sessizce devam et - tracking başarısız olsa bile iletişim çalışmalı
      });
    },
    [listing.id]
  );

  // Mobile: 2-step dialog state
  const [mobileStep, setMobileStep] = useState<"details" | "contact">("details");
  const [mobileDialogOpen, setMobileDialogOpen] = useState(false);

  const handleMobileDialogChange = (open: boolean) => {
    setMobileDialogOpen(open);
    if (!open) setMobileStep("details");
  };

  // Mobile Bottom Bar
  if (isMobile) {
    return (
      <div className="pb-safe fixed right-0 bottom-0 left-0 z-40 border-t bg-white px-4 py-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-lg font-bold">
              {formattedPrice}
              <span className="text-muted-foreground text-xs font-normal"> / gece</span>
            </p>
            <p className="text-primary text-xs font-medium">komisyonsuz fiyat</p>
          </div>
          <Dialog open={mobileDialogOpen} onOpenChange={handleMobileDialogChange}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-primary/90">İletişime Geç</Button>
            </DialogTrigger>
            <DialogContent className="max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {mobileStep === "details" ? "İletişim İçin Bilgiler" : "Ev Sahibiyle İletişim"}
                </DialogTitle>
              </DialogHeader>

              {mobileStep === "details" ? (
                <div className="space-y-5 pt-2">
                  {/* Tarih Seçimi - Tek range takvim */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <CalendarDays className="text-primary h-4 w-4" />
                      Tarihler
                    </div>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button className="grid w-full grid-cols-2 gap-2 text-left">
                          <div className="hover:bg-muted/50 rounded-lg border p-3 transition-colors">
                            <p className="text-muted-foreground text-[10px] font-medium uppercase">
                              Giriş
                            </p>
                            <p className="mt-0.5 text-sm font-medium">
                              {checkIn
                                ? format(checkIn, "d MMM yyyy", { locale: tr })
                                : "Tarih seçin"}
                            </p>
                          </div>
                          <div className="hover:bg-muted/50 rounded-lg border p-3 transition-colors">
                            <p className="text-muted-foreground text-[10px] font-medium uppercase">
                              Çıkış
                            </p>
                            <p className="mt-0.5 text-sm font-medium">
                              {checkOut
                                ? format(checkOut, "d MMM yyyy", { locale: tr })
                                : "Tarih seçin"}
                            </p>
                          </div>
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="center">
                        <CalendarComponent
                          mode="range"
                          selected={dateRange}
                          onSelect={handleDateSelect}
                          disabled={(date) => date < today || isDateBlocked(date)}
                          locale={tr}
                          numberOfMonths={1}
                          modifiers={{ booked: disabledDates }}
                          modifiersStyles={{
                            booked: {
                              textDecoration: "line-through",
                              color: "#ef4444",
                              backgroundColor: "#fef2f2",
                              borderRadius: "4px",
                            },
                          }}
                          initialFocus
                        />
                        <div className="border-t p-3">
                          <div className="flex items-center justify-center gap-4 text-xs">
                            <div className="flex items-center gap-1.5">
                              <div className="bg-primary/20 h-3 w-3 rounded" />
                              <span className="text-muted-foreground">Müsait</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <div className="h-3 w-3 rounded bg-red-100 line-through" />
                              <span className="text-muted-foreground">Dolu</span>
                            </div>
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Misafir Sayısı */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <Users className="text-primary h-4 w-4" />
                      Misafirler
                    </div>
                    <div className="flex items-center justify-between rounded-lg border p-3">
                      <span className="text-sm">Misafir Sayısı</span>
                      <div className="flex items-center gap-3">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8 rounded-full"
                          onClick={() => setGuests(Math.max(1, guests - 1))}
                          disabled={guests <= 1}
                        >
                          <Minus className="h-4 w-4" />
                        </Button>
                        <span className="w-6 text-center font-medium">{guests}</span>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8 rounded-full"
                          onClick={() => setGuests(Math.min(listing.capacity, guests + 1))}
                          disabled={guests >= listing.capacity}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    {listing.houseRules.petsAllowed && (
                      <div className="flex items-center justify-between rounded-lg border p-3">
                        <div className="flex items-center gap-2 text-sm">
                          <PawPrint className="text-muted-foreground h-4 w-4" />
                          Evcil Hayvan
                        </div>
                        <Switch
                          checked={hasPets}
                          onCheckedChange={setHasPets}
                          aria-label="Evcil hayvanla geliyorum"
                        />
                      </div>
                    )}
                    <p className="text-muted-foreground text-xs">
                      Maksimum {listing.capacity} misafir
                    </p>
                  </div>

                  {/* Fiyat Özeti */}
                  {nights > 0 && (
                    <div className="bg-muted/50 space-y-2 rounded-lg p-3 text-sm">
                      <div className="flex justify-between">
                        <span>
                          {hasDatePricing
                            ? `Konaklama (${nights} gece)`
                            : `${formattedPrice} × ${nights} gece`}
                        </span>
                        <span>{formatCurrency(totalAccommodation)}</span>
                      </div>
                      {cleaningFee > 0 && (
                        <div className="text-muted-foreground flex justify-between">
                          <span>Temizlik ücreti</span>
                          <span>{formatCurrency(cleaningFee)}</span>
                        </div>
                      )}
                      <div className="flex justify-between border-t pt-2 font-semibold">
                        <span>Toplam</span>
                        <span>{formatCurrency(grandTotal)}</span>
                      </div>
                      {!isMinimumMet && (
                        <p className="text-destructive text-xs">
                          Minimum {listing.priceInfo.minimumStay} gece konaklama gereklidir.
                        </p>
                      )}
                      {aralikUyarisi && (
                        <p className="text-destructive text-xs" role="alert">
                          {aralikUyarisi}
                        </p>
                      )}
                    </div>
                  )}

                  <Button
                    className="bg-primary hover:bg-primary/90 w-full gap-2"
                    onClick={() => setMobileStep("contact")}
                  >
                    Devam Et
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="space-y-3 pt-2">
                  {/* Seçilen bilgiler özeti */}
                  {(checkIn || checkOut || guests > 1) && (
                    <div className="bg-muted/50 text-muted-foreground space-y-1 rounded-lg p-3 text-xs">
                      {checkIn && checkOut && (
                        <p>
                          {format(checkIn, "d MMM", { locale: tr })} -{" "}
                          {format(checkOut, "d MMM", { locale: tr })} ({nights} gece)
                        </p>
                      )}
                      <p>
                        {guests} misafir{hasPets ? " + evcil hayvan" : ""}
                      </p>
                    </div>
                  )}

                  {whatsAppLink && (
                    <a
                      href={whatsAppLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackContactClick("whatsapp")}
                      className="hover:bg-muted flex items-center gap-3 rounded-lg border p-3 transition-colors"
                    >
                      <WhatsAppIcon className="h-5 w-5 text-green-600" />
                      <span className="flex-1 font-medium">WhatsApp ile mesaj at</span>
                    </a>
                  )}
                  {listing.owner?.phone && (
                    <a
                      href={`tel:${listing.owner?.phone}`}
                      onClick={() => trackContactClick("phone")}
                      className="hover:bg-muted flex items-center gap-3 rounded-lg border p-3 transition-colors"
                    >
                      <Phone className="text-primary h-5 w-5" />
                      <span className="flex-1 font-medium">Telefon ile ara</span>
                    </a>
                  )}
                  {listing.owner?.email && (
                    <a
                      href={getEmailLink()}
                      onClick={() => trackContactClick("email")}
                      className="hover:bg-muted flex items-center gap-3 rounded-lg border p-3 transition-colors"
                    >
                      <Mail className="text-primary h-5 w-5" />
                      <span className="flex-1 font-medium">E-posta gönder</span>
                    </a>
                  )}
                  {listing.owner?.website && (
                    <a
                      href={`https://${listing.owner?.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackContactClick("website")}
                      className="hover:bg-muted flex items-center gap-3 rounded-lg border p-3 transition-colors"
                    >
                      <Globe className="text-primary h-5 w-5" />
                      <span className="flex-1 font-medium">Web sitesini ziyaret et</span>
                    </a>
                  )}
                  <p className="text-muted-foreground pt-1 text-center text-xs">
                    Ödeme, mülk sahibi ile doğrudan gerçekleşir. konakla ödeme tahsilatı yapmaz,
                    konaklama hizmeti sunmaz.
                  </p>
                  <button
                    onClick={() => setMobileStep("details")}
                    className="text-primary w-full py-1 text-center text-xs font-medium"
                  >
                    ← Bilgileri Düzenle
                  </button>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>
    );
  }

  // Desktop Sidebar
  return (
    <div className="sticky top-24 rounded-xl border p-6 shadow-sm">
      {/* Fiyat */}
      <div className="flex items-baseline gap-1">
        <span className="text-2xl font-bold">{formattedPrice}</span>
        <span className="text-muted-foreground">/ gece</span>
        <Badge variant="secondary" className="bg-primary/10 text-primary ml-2">
          komisyonsuz fiyat
        </Badge>
      </div>

      {/* Tarih ve Misafir Seçimi */}
      <div className="mt-4 space-y-3">
        {/* Tek range takvim - 2 ay yan yana */}
        <Popover>
          <PopoverTrigger asChild>
            <button className="grid w-full grid-cols-2 rounded-lg border text-left">
              <div className="hover:bg-muted/50 border-r p-3 transition-colors">
                <p className="text-muted-foreground text-xs font-medium">GİRİŞ</p>
                <p className="mt-1 text-sm">
                  {checkIn ? format(checkIn, "d MMM", { locale: tr }) : "Tarih seçin"}
                </p>
              </div>
              <div className="hover:bg-muted/50 p-3 transition-colors">
                <p className="text-muted-foreground text-xs font-medium">ÇIKIŞ</p>
                <p className="mt-1 text-sm">
                  {checkOut ? format(checkOut, "d MMM", { locale: tr }) : "Tarih seçin"}
                </p>
              </div>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="center">
            <CalendarComponent
              mode="range"
              selected={dateRange}
              onSelect={handleDateSelect}
              disabled={(date) => date < today || isDateBlocked(date)}
              locale={tr}
              numberOfMonths={2}
              modifiers={{
                booked: disabledDates,
              }}
              modifiersStyles={{
                booked: {
                  textDecoration: "line-through",
                  color: "#ef4444",
                  backgroundColor: "#fef2f2",
                  borderRadius: "4px",
                },
              }}
              initialFocus
            />
            <div className="border-t p-3">
              <div className="flex items-center justify-center gap-4 text-xs">
                <div className="flex items-center gap-1.5">
                  <div className="bg-primary/20 h-3 w-3 rounded" />
                  <span className="text-muted-foreground">Müsait</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="h-3 w-3 rounded bg-red-100 line-through" />
                  <span className="text-muted-foreground">Dolu</span>
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>

        {/* Misafir Seçimi */}
        <Popover>
          <PopoverTrigger asChild>
            <button className="hover:bg-muted/50 w-full rounded-lg border p-3 text-left transition-colors">
              <p className="text-muted-foreground text-xs font-medium">MİSAFİRLER</p>
              <p className="mt-1 text-sm">
                {guests} misafir{hasPets ? " + evcil hayvan" : ""}
              </p>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-72" align="start">
            <div className="space-y-4">
              {/* Misafir Sayısı */}
              <div className="flex items-center justify-between">
                <span className="font-medium">Misafir Sayısı</span>
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 rounded-full"
                    onClick={() => setGuests(Math.max(1, guests - 1))}
                    disabled={guests <= 1}
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <span className="w-6 text-center font-medium">{guests}</span>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 rounded-full"
                    onClick={() => setGuests(Math.min(listing.capacity, guests + 1))}
                    disabled={guests >= listing.capacity}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Evcil Hayvan */}
              <div className="flex items-center justify-between border-t pt-4">
                <span className="font-medium">Evcil Hayvan</span>
                <Switch
                  checked={hasPets}
                  onCheckedChange={setHasPets}
                  aria-label="Evcil hayvanla geliyorum"
                />
              </div>

              <p className="text-muted-foreground text-xs">Maksimum {listing.capacity} misafir</p>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Minimum konaklama */}
      <p className="text-muted-foreground mt-3 text-sm">
        Minimum {listing.priceInfo.minimumStay} gece konaklama
      </p>

      {/* Fiyat Detayı (tarih seçildiğinde) */}
      {nights > 0 && (
        <div className="mt-4 space-y-2 border-t pt-4 text-sm">
          <div className="flex justify-between">
            <span>
              {hasDatePricing ? `Konaklama (${nights} gece)` : `${formattedPrice} × ${nights} gece`}
            </span>
            <span>{formatCurrency(totalAccommodation)}</span>
          </div>
          {cleaningFee > 0 && (
            <div className="text-muted-foreground flex justify-between">
              <span>Temizlik ücreti</span>
              <span>{formatCurrency(cleaningFee)}</span>
            </div>
          )}
          <div className="flex justify-between border-t pt-2 font-semibold">
            <span>Toplam</span>
            <span>{formatCurrency(grandTotal)}</span>
          </div>
          {!isMinimumMet && (
            <p className="text-destructive mt-2 text-xs">
              Minimum {listing.priceInfo.minimumStay} gece konaklama gereklidir.
            </p>
          )}
          {aralikUyarisi && (
            <p className="text-destructive mt-2 text-xs" role="alert">
              {aralikUyarisi}
            </p>
          )}
        </div>
      )}

      {/* İletişim Butonları */}
      <div className="mt-6 space-y-2">
        <p className="text-center text-sm font-medium">İletişim</p>

        {whatsAppLink && (
          <a
            href={whatsAppLink}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackContactClick("whatsapp")}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-green-600 px-4 py-3 font-medium text-white transition-colors hover:bg-green-700"
          >
            <WhatsAppIcon className="h-5 w-5" />
            WhatsApp
          </a>
        )}

        <div className="flex gap-2">
          {listing.owner?.phone && (
            <a
              href={`tel:${listing.owner?.phone}`}
              onClick={() => trackContactClick("phone")}
              className="hover:bg-muted flex flex-1 items-center justify-center gap-2 rounded-lg border px-4 py-3 font-medium transition-colors"
            >
              <Phone className="h-5 w-5" />
              Ara
            </a>
          )}
          {listing.owner?.email && (
            <a
              href={getEmailLink()}
              onClick={() => trackContactClick("email")}
              className="hover:bg-muted flex flex-1 items-center justify-center gap-2 rounded-lg border px-4 py-3 font-medium transition-colors"
            >
              <Mail className="h-5 w-5" />
              E-posta
            </a>
          )}
        </div>

        {listing.owner?.website && (
          <a
            href={`https://${listing.owner?.website}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackContactClick("website")}
            className="hover:bg-muted flex w-full items-center justify-center gap-2 rounded-lg border px-4 py-3 font-medium transition-colors"
          >
            <Globe className="h-5 w-5" />
            Web Sitesi
          </a>
        )}
      </div>

      {/* Uyarı */}
      <div className="mt-4 text-center">
        <p className="text-muted-foreground text-xs">
          Ödeme, mülk sahibi ile doğrudan gerçekleşir.
          <br />
          konakla ödeme tahsilatı yapmaz, konaklama hizmeti sunmaz.
        </p>
      </div>
    </div>
  );
}
